import dayjs from "dayjs"
import { z } from "zod"

import { hasSignificantFigures } from "../TestTemplates/utils"

import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"
import { FieldGroupEnum } from "@/graphql/codegen/graphql"
import { isNumericString } from "@/utils/forms"
import { validateNumericField, validateStringMaxLength, validateValueInRange } from "@/utils/schema"

const RESULT_VALUE_MAX_LENGTH = 10 // AS400 constraint

const RESULT_VALUE_MIN = -99999.99999 // AS400 constraint

const RESULT_VALUE_MAX = 99999.99999 // AS400 constraint

type ProductTestFormSchema = z.ZodObject<{
  lotNumber: z.ZodString
  batchNumber: z.ZodString
  orderId: z.ZodString
  manufacturingDate: z.ZodString
  expirationDate: z.ZodString
  status: z.ZodString
  results: z.ZodArray<
    z.ZodObject<{
      productTestResultId: z.ZodOptional<z.ZodString>
      associatedTestTemplateId: z.ZodString
      value: z.ZodString
      printToCoa: z.ZodBoolean
    }>
  >
  comment: z.ZodString
  documents: z.ZodArray<
    z.ZodEffects<
      z.ZodObject<{
        type: z.ZodUnion<[z.ZodNativeEnum<typeof FieldGroupEnum>, z.ZodLiteral<"">]>
        description: z.ZodString
        file: z.ZodNullable<z.ZodType<File>>
      }>
    >
  >
}>

const productTestResultFormSchema = z.object({
  productTestResultId: z.string().optional(),
  associatedTestTemplateId: z.string(),
  value: z.string(),
  printToCoa: z.boolean(),
})

const productTestDocumentFormSchema = z
  .object({
    type: z.union([z.nativeEnum(FieldGroupEnum), z.literal("")]),
    description: z.string(),
    file: z.instanceof(File).nullable(),
  })
  .refine((data) => (data.file ? !!data.type : true), {
    message: "Document Type is required when a file is selected",
    path: ["type"],
  })

const validateDateRange = (data: ProductTestFormValues, ctx: z.RefinementCtx): void => {
  const manufacturingDate = dayjs(data.manufacturingDate)
  const expirationDate = dayjs(data.expirationDate)

  if (manufacturingDate.isValid() && expirationDate.isValid() && manufacturingDate.isAfter(expirationDate)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Manufacturing Date must be before Expiration Date",
      path: ["manufacturingDate"],
    })
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Expiration Date must be after Manufacturing Date",
      path: ["expirationDate"],
    })
  }
}

const validateTestResults = (
  associatedTestTemplates: AssociatedTestTemplate[],
  data: ProductTestFormValues,
  ctx: z.RefinementCtx
): void =>
  data.results.forEach((result, index) => {
    if (!(result.associatedTestTemplateId && result.value)) return

    const associatedTestTemplate = associatedTestTemplates.find(
      (template) => template.associatedTestTemplateId === result.associatedTestTemplateId
    )

    if (
      associatedTestTemplate &&
      hasSignificantFigures(associatedTestTemplate.testTemplate!.comparison, associatedTestTemplate.dataType)
    ) {
      const isNumericFieldPassed = validateNumericField(result.value, "Value", ctx, ["results", index, "value"])

      if (!isNumericFieldPassed) return

      const isValueInRangePassed = validateValueInRange(
        result.value,
        "Value",
        RESULT_VALUE_MIN,
        RESULT_VALUE_MAX,
        ctx,
        ["results", index, "value"]
      )

      if (!isValueInRangePassed) return

      const fractionDigits = result.value.split(".")[1] || ""
      const significantFigures = associatedTestTemplate.significantFigures ?? 0

      if (fractionDigits.length !== significantFigures) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Value must have ${significantFigures} decimal place${significantFigures === 1 ? "" : "s"}`,
          path: ["results", index, "value"],
        })
      }
    }

    if (isNumericString(result.value)) {
      validateValueInRange(result.value, "Value", RESULT_VALUE_MIN, RESULT_VALUE_MAX, ctx, ["results", index, "value"])
    } else {
      validateStringMaxLength(result.value, "Value", RESULT_VALUE_MAX_LENGTH, ctx, ["results", index, "value"])
    }
  })

export const createProductTestFormSchema = (
  associatedTestTemplates: AssociatedTestTemplate[]
): z.ZodEffects<ProductTestFormSchema> => {
  return z
    .object({
      lotNumber: z.string().min(1, { message: "Lot # is required" }).max(20, "Lot # must not exceed 20 characters"), // AS400 constraint
      batchNumber: z.string().max(10, "Batch # must not exceed 10 characters"), // AS400 constraint
      orderId: z.string().max(10, "Purchase Order # must not exceed 10 characters"), // AS400 constraint
      manufacturingDate: z.string().min(1, { message: "Manufacturing Date is required" }),
      expirationDate: z.string().min(1, { message: "Expiration Date is required" }),
      status: z.string().min(1, { message: "Status is required" }),
      results: z.array(productTestResultFormSchema),
      comment: z.string().max(240, "Comments must not exceed 240 characters"), // AS400 constraint (6 × 40 char fields)
      documents: z.array(productTestDocumentFormSchema),
    })
    .superRefine((data, ctx) => {
      validateDateRange(data, ctx)
      validateTestResults(associatedTestTemplates, data, ctx)
    })
}

export const OverrideTestResultsSchema = z.object({
  documents: z.array(productTestDocumentFormSchema),
})

export type OverrideTestResultsFormData = z.infer<typeof OverrideTestResultsSchema>

export type ProductTestResultFormValues = z.infer<typeof productTestResultFormSchema>

export type ProductTestDocumentFormValues = z.infer<typeof productTestDocumentFormSchema>

export type ProductTestFormValues = z.infer<ReturnType<typeof createProductTestFormSchema>>
